import { useEffect, useMemo, useState } from "react";
import api, { BACKEND_URL } from "../lib/api";
import { X, Download, Banknote, Smartphone, Wallet, AlertTriangle } from "lucide-react";
import { ddmmyyyy } from "../lib/dates";

const PERIODS = [
    { value: "today", label: "Today" },
    { value: "7d", label: "Last 7 days" },
    { value: "30d", label: "Last 30 days" },
    { value: "mtd", label: "This month" },
    { value: "all", label: "All-time" },
];

/**
 * Iter 32 / M3 — Manager Collections drill-down.
 * Triggered by clicking the "My Collections" card on Manager Dashboard.
 * Shows ₹ totals (Total / Cash / UPI), period filter, customer breakdown,
 * pending ground collection (A9), CSV download.
 */
export default function ManagerCollectionsModal({ onClose, initialFocus = "total" }) {
    const [period, setPeriod] = useState("all");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [focus, setFocus] = useState(initialFocus); // total | cash | upi | pending_ground

    useEffect(() => {
        let alive = true;
        const run = async () => {
            setLoading(true);
            try {
                const { data } = await api.get("/manager/collections", { params: { period } });
                if (alive) setData(data);
            } catch (_) { /* ignore */ }
            finally { if (alive) setLoading(false); }
        };
        run();
        return () => { alive = false; };
    }, [period]);

    const downloadCsv = () => {
        const params = new URLSearchParams({ period }).toString();
        const url = `${BACKEND_URL}/api/manager/collections.csv?${params}`;
        const token = localStorage.getItem("dok_token");
        // Use fetch+blob so we can attach the bearer token (the export sits behind manager auth)
        fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
            .then(r => r.blob())
            .then(blob => {
                const a = document.createElement("a");
                const u = URL.createObjectURL(blob);
                a.href = u;
                a.download = `manager-collections-${period}.csv`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                setTimeout(() => URL.revokeObjectURL(u), 1500);
            }).catch(() => {});
    };

    const filteredCustomers = useMemo(() => {
        if (!data) return [];
        if (focus === "cash") return data.customers.filter(c => c.cash > 0);
        if (focus === "upi") return data.customers.filter(c => c.upi > 0);
        return data.customers;
    }, [data, focus]);

    return (
        <div className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4" data-testid="mgr-collections-modal">
            <div className="w-full max-w-4xl max-h-[92vh] overflow-y-auto bg-slate-900 border border-slate-800 rounded-lg p-4 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h3 className="font-display text-xl sm:text-2xl font-bold uppercase text-white">My Collections</h3>
                        <p className="text-xs text-slate-500 mt-1">All amounts you personally collected (cash + UPI). Excludes amounts collected by admins.</p>
                    </div>
                    <button onClick={onClose} className="text-slate-500 hover:text-white" data-testid="mgr-collections-close"><X className="w-5 h-5" /></button>
                </div>

                <div className="flex flex-wrap items-end gap-3 mb-4">
                    <div>
                        <label className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500">Period</label>
                        <select value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="mgr-collections-period"
                            className="block mt-1 h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white text-sm min-w-[180px]">
                            {PERIODS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                        </select>
                    </div>
                    <button onClick={downloadCsv} data-testid="mgr-collections-download"
                        className="h-10 px-4 inline-flex items-center gap-2 rounded text-[11px] font-bold uppercase tracking-widest bg-[#00FF66] text-black hover:bg-[#00cc52]">
                        <Download className="w-3.5 h-3.5" /> Download CSV
                    </button>
                </div>

                {loading || !data ? (
                    <div className="text-slate-500 py-8 text-center">Loading…</div>
                ) : (
                    <>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
                            <Stat label="Total" amount={data.total} icon={Wallet} active={focus === "total"} onClick={() => setFocus("total")} testid="mgr-collect-total" accent="green" />
                            <Stat label="Cash" amount={data.cash} icon={Banknote} active={focus === "cash"} onClick={() => setFocus("cash")} testid="mgr-collect-cash" accent="green" />
                            <Stat label="UPI" amount={data.upi} icon={Smartphone} active={focus === "upi"} onClick={() => setFocus("upi")} testid="mgr-collect-upi" accent="blue" />
                            <Stat label="Pending Ground" amount={data.pending_ground} icon={AlertTriangle} active={focus === "pending_ground"} onClick={() => setFocus("pending_ground")} testid="mgr-collect-pending" accent="amber" />
                        </div>

                        {focus === "pending_ground" ? (
                            <PendingGroundTable rows={data.pending_ground_customers} />
                        ) : (
                            <CustomersTable rows={filteredCustomers} focus={focus} />
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

function Stat({ label, amount, icon: Icon, active, onClick, testid, accent }) {
    const cls = accent === "green" ? "text-[#00FF66] border-[#00FF66]/40" : accent === "blue" ? "text-cyan-400 border-cyan-700/50" : accent === "amber" ? "text-amber-400 border-amber-700/50" : "text-white border-slate-800";
    return (
        <button onClick={onClick} data-testid={testid}
            className={`text-left bg-slate-950 border ${cls} rounded-lg p-4 transition ${active ? "ring-2 ring-[#00FF66]/40" : "opacity-80 hover:opacity-100"}`}>
            <div className="flex items-center justify-between">
                <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400">{label}</div>
                <Icon className={`w-4 h-4 ${cls.split(" ")[0]}`} />
            </div>
            <div className={`mt-2 font-display text-2xl font-bold ${cls.split(" ")[0]}`}>₹{(amount || 0).toLocaleString("en-IN")}</div>
        </button>
    );
}

function CustomersTable({ rows, focus }) {
    if (!rows.length) {
        return <div className="text-slate-500 py-8 text-center text-sm">No collections in this view.</div>;
    }
    return (
        <div className="overflow-x-auto" data-testid="mgr-customers-table">
            <table className="w-full text-sm">
                <thead>
                    <tr className="text-slate-400 text-xs uppercase tracking-widest border-b border-slate-800">
                        <th className="text-left py-2 px-2">Customer</th>
                        <th className="text-left py-2 px-2">Phone</th>
                        <th className="text-right py-2 px-2">Bookings</th>
                        <th className="text-right py-2 px-2">Cash ₹</th>
                        <th className="text-right py-2 px-2">UPI ₹</th>
                        <th className="text-right py-2 px-2">Total ₹</th>
                    </tr>
                </thead>
                <tbody className="text-slate-200">
                    {rows.map((c, i) => (
                        <tr key={i} className="border-b border-slate-900" data-testid={`mgr-cust-row-${i}`}>
                            <td className="py-2 px-2">{c.customer_name}</td>
                            <td className="py-2 px-2 text-slate-400">{c.customer_phone || "—"}</td>
                            <td className="py-2 px-2 text-right">{c.count}</td>
                            <td className={`py-2 px-2 text-right ${focus === "cash" ? "text-[#00FF66] font-bold" : ""}`}>₹{c.cash.toLocaleString("en-IN")}</td>
                            <td className={`py-2 px-2 text-right ${focus === "upi" ? "text-cyan-400 font-bold" : ""}`}>₹{c.upi.toLocaleString("en-IN")}</td>
                            <td className="py-2 px-2 text-right font-bold text-white">₹{c.total.toLocaleString("en-IN")}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function PendingGroundTable({ rows }) {
    if (!rows || !rows.length) {
        return <div className="text-slate-500 py-8 text-center text-sm">No pending ground collection.</div>;
    }
    return (
        <div className="overflow-x-auto" data-testid="mgr-pending-ground-table">
            <table className="w-full text-sm">
                <thead>
                    <tr className="text-slate-400 text-xs uppercase tracking-widest border-b border-slate-800">
                        <th className="text-left py-2 px-2">Customer</th>
                        <th className="text-left py-2 px-2">Phone</th>
                        <th className="text-left py-2 px-2">Centre</th>
                        <th className="text-left py-2 px-2">Code</th>
                        <th className="text-left py-2 px-2">Date</th>
                        <th className="text-left py-2 px-2">Source</th>
                        <th className="text-right py-2 px-2">Amount ₹</th>
                    </tr>
                </thead>
                <tbody className="text-slate-200">
                    {rows.map((r, i) => (
                        <tr key={i} className="border-b border-slate-900" data-testid={`mgr-pending-row-${i}`}>
                            <td className="py-2 px-2">{r.customer_name}</td>
                            <td className="py-2 px-2 text-slate-400">{r.customer_phone || "—"}</td>
                            <td className="py-2 px-2">{r.centre_name}</td>
                            <td className="py-2 px-2 font-mono text-xs text-[#00FF66]">{r.booking_code}</td>
                            <td className="py-2 px-2 text-slate-400">{ddmmyyyy(r.booking_date)}</td>
                            <td className="py-2 px-2 text-amber-400 text-xs">{r.external_source || "—"}</td>
                            <td className="py-2 px-2 text-right font-bold text-amber-400">₹{r.amount.toLocaleString("en-IN")}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
